import type {
  AuthResponse,
  AuthUser,
  SyncPullResponse,
  SyncPushResponse,
} from "@lockcal/shared";

const TOKEN_KEY = "lockcal.token";
const USER_KEY = "lockcal.user";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "ApiError";
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser | null): void {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  options: { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  if (options.auth !== false) {
    const token = getToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
  }

  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new ApiError(res.status, err?.error ?? "unknown", err?.message);
  }
  return body as T;
}

export const api = {
  health: () => request<{ ok: boolean }>("/api/health", {}, { auth: false }),

  register: (email: string, password: string, displayName?: string) =>
    request<AuthResponse>(
      "/api/auth/register",
      {
        method: "POST",
        body: JSON.stringify({ email, password, displayName }),
      },
      { auth: false },
    ),

  login: (email: string, password: string) =>
    request<AuthResponse>(
      "/api/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
      { auth: false },
    ),

  google: (idToken: string) =>
    request<AuthResponse>(
      "/api/auth/google",
      { method: "POST", body: JSON.stringify({ idToken }) },
      { auth: false },
    ),

  /** Trades the one-time code of the Google round trip for a session. */
  googleExchange: (code: string) =>
    request<AuthResponse>(
      "/api/google/app/exchange",
      { method: "POST", body: JSON.stringify({ code }) },
      { auth: false },
    ),

  me: () => request<{ user: AuthUser }>("/api/auth/me"),

  googleStatus: () =>
    request<{
      configured: boolean;
      connected: boolean;
      email: string | null;
      calendarId: string;
      lastSyncAt: number | null;
      lastError: string | null;
      needsReauth: boolean;
    }>("/api/google/status"),

  googleAuthUrl: () =>
    request<{ url: string; redirectUri: string }>("/api/google/auth-url"),

  googleCalendars: () =>
    request<{ calendars: { id: string; summary: string; primary: boolean }[] }>(
      "/api/google/calendars",
    ),

  googleSetCalendar: (calendarId: string) =>
    request<{ ok: true }>("/api/google/calendar", {
      method: "POST",
      body: JSON.stringify({ calendarId }),
    }),

  googleSync: () =>
    request<{ ok: true; pushed: number; pulled: number; rev: number }>(
      "/api/google/sync",
      { method: "POST" },
    ),

  googleDisconnect: () =>
    request<{ ok: true }>("/api/google", { method: "DELETE" }),

  /** Public sign-in options, currently the Google client id. */
  authConfig: () =>
    request<{ googleClientId: string | null; googleEnabled: boolean }>(
      "/api/auth/config",
      {},
      { auth: false },
    ),

  /** Deletes the account and every record that belongs to it. */
  deleteAccount: () => request<void>("/api/auth/me", { method: "DELETE" }),

  pull: (since: number) =>
    request<SyncPullResponse>(`/api/sync/pull?since=${since}`),

  push: (deviceId: string, changes: unknown[]) =>
    request<SyncPushResponse>("/api/sync/push", {
      method: "POST",
      body: JSON.stringify({ deviceId, changes }),
    }),
};

export { TOKEN_KEY, USER_KEY };
